import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Chip from "@mui/joy/Chip";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Stack from "@mui/joy/Stack";
import Textarea from "@mui/joy/Textarea";
import Typography from "@mui/joy/Typography";
import React from "react";
import { StudyContext } from "../pages/StudyPage";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import { StudyQuartzClientContext } from "../../../shared/services/api/contexts";
import { AssociatedParty, StudyDesign, StudyPhase } from "../../../models/study";
import { DrawerWrapper } from "../../../shared/components/layout/DrawerWrapper";

interface StudyEditDrawerProps {
  readonly onClose: () => void;
  readonly refresh: () => void;
}

export default function StudyEditDrawer({ onClose, refresh }: StudyEditDrawerProps) {
  const { showSnackbar } = useSnackbar();
  const study = React.useContext(StudyContext);
  const studyQuartzClient = React.useContext(StudyQuartzClientContext);

  const [updatedStudy, setUpdatedStudy] = React.useState(study);
  const [isSaving, setIsSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);

  // helper to get an AssociatedParty by role
  const getParty = (role: AssociatedParty["role"]): AssociatedParty | null =>
    updatedStudy.associated_parties.find((ap) => ap.role === role) ?? null;

  // helper to create updated associated parties array
  const createUpdatedParties = (
    currentParties: AssociatedParty[],
    role: AssociatedParty["role"],
    newName: string
  ): AssociatedParty[] => {
    const others = currentParties.filter((ap) => ap.role !== role);

    if (!newName.trim()) {
      return others;
    }

    return [
      ...others,
      {
        role,
        name: newName,
        party: null,
        period: null,
      },
    ];
  };

  // generic handler to set/update an AssociatedParty's name
  const handlePartyNameChange =
    (role: AssociatedParty["role"]) => (evt: React.ChangeEvent<HTMLInputElement>) => {
      const newName = evt.target.value;
      setUpdatedStudy((prev) => ({
        ...prev,
        associated_parties: createUpdatedParties(prev.associated_parties, role, newName),
      }));
    };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await studyQuartzClient.updateStudy(study.study_id, updatedStudy);
      showSnackbar({
        text: "Study updated successfully",
        color: "success",
      });
      refresh();
    } catch (error) {
      console.error("Failed to update study:", error);
      showSnackbar({
        text: "Failed to update study",
        color: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  React.useEffect(() => {
    setHasChanges(JSON.stringify(updatedStudy) !== JSON.stringify(study));
  }, [updatedStudy, study]);

  const sponsor = getParty("sponsor");
  const principalInvestigator = getParty("principal-investigator");

  return (
    <DrawerWrapper>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: "white",
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
          <Typography level="title-md" sx={{ flex: 1 }}>
            Manage questions
          </Typography>
          <IconButton component="span" variant="plain" color="neutral" size="sm" onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Divider />
        {/* Main Content (scrollable area) */}
        <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}>
          <Stack direction="column" gap={2}>
            <FormControl>
              <FormLabel>Acronym</FormLabel>
              <Input
                value={updatedStudy.acronym ? updatedStudy.acronym : ""}
                onChange={(event) =>
                  setUpdatedStudy({ ...updatedStudy, acronym: event.target.value })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Title</FormLabel>
              <Textarea
                value={updatedStudy.title}
                onChange={(event) =>
                  setUpdatedStudy({ ...updatedStudy, title: event.target.value })
                }
                minRows={3}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={updatedStudy.description ? updatedStudy.description : ""}
                onChange={(event) =>
                  setUpdatedStudy({ ...updatedStudy, description: event.target.value })
                }
                minRows={3}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Sponsor</FormLabel>
              <Input
                placeholder="Name of sponsoring organization"
                value={sponsor?.name || ""}
                onChange={handlePartyNameChange("sponsor")}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Principal Investigator</FormLabel>
              <Input
                placeholder="Name of PI"
                value={principalInvestigator?.name || ""}
                onChange={handlePartyNameChange("principal-investigator")}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Phase</FormLabel>
              <Select
                defaultValue={study.phase}
                onChange={(event, newValue) =>
                  setUpdatedStudy({ ...updatedStudy, phase: newValue as StudyPhase })
                }
              >
                <Option value="n-a">N/A</Option>
                <Option value="early-phase-1">Early Phase 1</Option>
                <Option value="phase-1">Phase 1</Option>
                <Option value="phase-1-phase-2">Phase 1/2</Option>
                <Option value="phase-2">Phase 2</Option>
                <Option value="phase-2-phase-3">Phase 2/3</Option>
                <Option value="phase-3">Phase 3</Option>
                <Option value="phase-4">Phase 4</Option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Study design</FormLabel>
              <Select
                multiple
                defaultValue={study.study_design}
                onChange={(event, selected) =>
                  setUpdatedStudy({ ...updatedStudy, study_design: selected as StudyDesign[] })
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", gap: "0.25rem" }}>
                    {selected.map((selectedOption) => (
                      <Chip key={selectedOption.value} variant="soft" color="primary">
                        {selectedOption.label}
                      </Chip>
                    ))}
                  </Box>
                )}
                sx={{
                  minWidth: "15rem",
                }}
                slotProps={{
                  listbox: {
                    sx: {
                      width: "100%",
                    },
                  },
                }}
              >
                <Option value="observational">Observational</Option>
                <Option value="interventional">Interventional</Option>
                <Option value="retropsective">Retropsective</Option>
                <Option value="prospective">Prospective</Option>
                <Option value="randomized">Randomized</Option>
                <Option value="non-randomized">Non randomized</Option>
                <Option value="real-world-data">Real world data</Option>
              </Select>
            </FormControl>
          </Stack>
        </Box>
        {/* Footer: Save Button */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
          <Button
            variant="solid"
            fullWidth
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            loading={isSaving}
          >
            Save
          </Button>
        </Box>
      </Box>
    </DrawerWrapper>
  );
}
